import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HELP_TIPS, type TipKey } from '../services/tour';
import { trackEvent } from '../services/analytics';

interface HelpTipProps {
  tipKey: TipKey;
  className?: string;
  label?: string;
}

const POPOVER_WIDTH = 280;
const POPOVER_GAP = 8;
const VIEWPORT_MARGIN = 12;

type Placement = 'top' | 'bottom';

interface ResolvedPos {
  top: number;
  left: number;
  placement: Placement;
}

function resolvePosition(button: HTMLButtonElement, popover: HTMLDivElement | null): ResolvedPos {
  const r = button.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const popoverH = popover?.offsetHeight ?? 100;
  const spaceBelow = vh - r.bottom;
  const spaceAbove = r.top;
  const placement: Placement = spaceBelow < popoverH + POPOVER_GAP + VIEWPORT_MARGIN && spaceAbove > spaceBelow
    ? 'top'
    : 'bottom';

  const popoverLeftRaw = r.left + r.width / 2 - POPOVER_WIDTH / 2;
  const popoverLeft = Math.max(
    VIEWPORT_MARGIN,
    Math.min(popoverLeftRaw, vw - POPOVER_WIDTH - VIEWPORT_MARGIN)
  );

  if (placement === 'top') {
    return {
      top: r.top - popoverH - POPOVER_GAP,
      left: popoverLeft,
      placement: 'top',
    };
  }
  return {
    top: r.bottom + POPOVER_GAP,
    left: popoverLeft,
    placement: 'bottom',
  };
}

export default function HelpTip({ tipKey, className = '', label = 'Help' }: HelpTipProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<ResolvedPos | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setPos(null);
      return;
    }
    setPos(resolvePosition(buttonRef.current, popoverRef.current));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const recompute = () => {
      if (buttonRef.current) {
        setPos(resolvePosition(buttonRef.current, popoverRef.current));
      }
    };
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const content = HELP_TIPS[tipKey];
  if (!content) return null;

  const handleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) {
        trackEvent('help_tip_opened', { tip_key: tipKey });
      }
      return next;
    });
  };

  const popoverStyle: React.CSSProperties | undefined = pos
    ? {
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: POPOVER_WIDTH,
        maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
      }
    : {
        position: 'fixed',
        top: -9999,
        left: -9999,
        width: POPOVER_WIDTH,
        visibility: 'hidden',
      };

  return (
    <span className={`relative inline-flex ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        onMouseEnter={() => {
          if (open) return;
          setOpen(true);
          trackEvent('help_tip_opened', { tip_key: tipKey });
        }}
        aria-label={label}
        aria-expanded={open}
        data-testid={`help-tip-${tipKey}`}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-500 hover:bg-parksy-100 hover:text-parksy-700 border border-gray-200"
      >
        ?
      </button>
      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            role="tooltip"
            data-testid={`help-popover-${tipKey}`}
            style={popoverStyle}
            className="z-[110] bg-white border border-gray-200 rounded-xl shadow-xl p-3 text-left"
          >
            <div className="font-semibold text-gray-900 text-sm mb-1">{content.title}</div>
            <div className="text-xs text-gray-600 leading-relaxed">{content.body}</div>
          </div>,
          document.body
        )}
    </span>
  );
}
